import { BaseImageCrop, CROP_KEY, CROP_RANGE } from '../../../image-operation/image-crop';
import { IShapeModel } from '../../../../Interface/shape-model.interface';
import { IImageCrop } from '../../../image-operation/interface/image-crop.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { IFillRect, IImageFill } from '../../fill.interface';
import { IShapeModelStyle } from '../../../../Interface/model-props/shape-model-props.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ISlideBaseAppServices } from '@src/slide/application/entry/toc/base/slide-base-app-services.interface';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';

const CROP_NORMAL = 'normal';

export class ImageFillCrop extends BaseImageCrop implements IImageCrop {
    private shapeModel: IShapeModel;
    public constructor(model: IShapeModel) {
        super(model);
        this.shapeModel = model;
    }

    /**
     * 裁剪或者裁剪为纵横比
     */
    public handleCrop(actionType?: string): void {
        if (!actionType || !CROP_RANGE.includes(actionType)) {
            return;
        }
        if (actionType !== `${CROP_KEY}-${CROP_NORMAL}`) {
            const [ow, oh] = actionType.replace(new RegExp(`${CROP_KEY}-`), '').split('-');
            this.cropWithWH(Number(ow), Number(oh));
        }
        // 形状图片填充-平铺，不支持裁剪
        const fillInfo = this.shapeModel.getBackgroundFill().getFillInfo();
        if (fillInfo?.image?.tile) {
            return;
        }

        this.getEditingStateManager().updateElementCropState(true);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_OBJ,
            action: TDWActionType.IMG_CUT,
        });
    }

    // 以ow,oh为比例进行裁剪
    private cropWithWH = (ow: number, oh: number): void => {
        const { width, height } = this.shapeModel.props.style;
        let { top, left } = this.shapeModel.props.style;
        let newWidth = width;
        let newHeight = height;

        // 裁剪比例与原图比例，记录以哪边条为准，同时把top, left更新
        const ratioNew = ow / oh;
        const ratioOld = width / height;
        let offsetX = 0;
        let offsetY = 0;
        if (ratioNew > ratioOld) {
            newHeight = width / ratioNew;
            offsetY = ((height - newHeight) >> 1);
            top += offsetY;
        } else {
            newWidth = height * ratioNew;
            offsetX = ((width - newWidth) >> 1);
            left += offsetX;
        }

        // 生成新的裁剪数据srcRect
        let srcRect = this.shapeModel.getBackgroundFill().getFillInfo()?.image?.stretch?.fillRect;
        if (!srcRect) {
            srcRect = {
                b: 0,
                t: 0,
                l: 0,
                r: 0,
            };
        } else {
            let { pl, pr, pt, pb } = this.shapeModel.getImageFillSize()
                .getBackgroundImageSize({ width, height, srcRect });
            pl += offsetX;
            pr += offsetX;
            pt += offsetY;
            pb += offsetY;
            srcRect.l = pl / (newWidth + pr + pl);
            srcRect.r = pr / (newWidth + pr + pl);
            srcRect.t = pt / (newHeight + pb + pt);
            srcRect.b = pb / (newHeight + pb + pt);
        }

        const size = { top, left, width: newWidth, height: newHeight };
        this.updateProps(size, srcRect);
    };

    private updateProps = (size: IRect, srcRect: Partial<IFillRect>) => {
        transaction(() => {
            const attrs = [{ style: { ...size } }];
            const fillInfo = this.shapeModel.getBackgroundFill()
                .getCurrentFill()?.fillInfo as IImageFill;
            this.shapeModel.getBackgroundFill().updateCurrentFill({ image: {
                ...fillInfo,
                stretch: {
                    fillRect: srcRect,
                },
            } });
            this.shapeModel.selectionService?.currentSlide?.updateElementsProps(attrs, [this.shapeModel]);
            this.getBorderSketchManager()?.updatePathListWhenSizeChange(
                this.shapeModel,
                attrs[0] as Partial<IShapeModelStyle>,
            );
        });
    };

    private getEditingStateManager() {
        const appServices = AppServiceProvider.getInstance().getAppServices() as ISlideBaseAppServices;
        return appServices.viewableServiceLayer.editingStateManager;
    }

    private getBorderSketchManager() {
        const appServices = AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
        return appServices.editableServiceLayer?.borderSketchManager;
    }
}
